/* <lineup-card> — Uniqlo-style colour-stepper product card.
   Loaded as a module by sections/main-collection-lineup.liquid. */

if (!customElements.get('lineup-card')) {
  // Swipe-hint nudge fires once per page — only the first multi-colour card.
  let lineupNudgeDone = false;

  class LineupCard extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.track = this.querySelector('.lineup-card__track');
      this.slides = Array.from(this.querySelectorAll('.lineup-card__slide'));
      this.fieldset = this.querySelector('.lineup-card__swatches');
      this.inputs = this.fieldset ? Array.from(this.fieldset.querySelectorAll('input')) : [];
      this.labels = this.fieldset ? Array.from(this.fieldset.querySelectorAll('label')) : [];
      this.titleLink = this.querySelector('[data-lineup-link]');
      this.selectedIndex = Math.max(0, this.inputs.findIndex((input) => input.checked));

      if (this.slides.length <= 1) return;

      this.addEventListener('pointerenter', () => this.warmUp(), { once: true });
      this.track.addEventListener('touchstart', () => this.warmUp(), { once: true, passive: true });

      this.querySelectorAll('.lineup-card__arrow').forEach((arrow) => {
        arrow.addEventListener('click', (event) => {
          event.preventDefault();
          const dir = parseInt(arrow.dataset.dir, 10);
          this.select((this.selectedIndex + dir + this.slides.length) % this.slides.length);
        });
      });

      this.labels.forEach((label, index) => {
        label.addEventListener('mouseenter', () => this.showSlide(index));
      });

      if (this.fieldset) {
        this.fieldset.addEventListener('mouseleave', () => this.showSlide(this.selectedIndex));
        this.fieldset.addEventListener('change', (event) => {
          const index = this.inputs.indexOf(event.target);
          if (index >= 0) this.select(index);
        });
      }

      // Touch swipe: when the snap scroll settles, commit the visible slide.
      // Only wired on touch devices — on hover devices the track is overflow
      // hidden (manual scroll impossible) and programmatic preview scrolls
      // must never commit.
      if (window.matchMedia('(hover: none)').matches) {
        // Swipe-hint nudge: the first time the card becomes mostly visible,
        // briefly shift the track to reveal the next colour's edge.
        if ('IntersectionObserver' in window && typeof this.track.animate === 'function') {
          const nudgeObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              nudgeObserver.disconnect();
              if (lineupNudgeDone) return;
              lineupNudgeDone = true;
              // Animate the slides (not the track) so the next colour's edge
              // peeks into the stationary scrollport. Scroll-snap must be
              // suspended meanwhile — mandatory snap chases the transform and
              // cancels the nudge visually.
              this.track.style.scrollSnapType = 'none';
              const animations = this.slides.map((slide) =>
                slide.animate(
                  [
                    { transform: 'translateX(0)' },
                    { transform: 'translateX(-28px)', offset: 0.45 },
                    { transform: 'translateX(-28px)', offset: 0.55 },
                    { transform: 'translateX(0)' }
                  ],
                  { duration: 800, easing: 'ease-in-out', delay: 200 }
                )
              );
              const restoreSnap = () => {
                this.track.style.scrollSnapType = '';
              };
              animations[0].onfinish = restoreSnap;
              animations[0].oncancel = restoreSnap;
            });
          }, { threshold: 0.6 });
          nudgeObserver.observe(this);
        }

        let scrollTimer;
        this.track.addEventListener(
          'scroll',
          () => {
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => {
              const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
              if (index !== this.selectedIndex && this.slides[index]) this.commit(index);
            }, 80);
          },
          { passive: true }
        );
      }

      if (this.selectedIndex > 0) {
        // Layout may not be final on the first frame (scrollLeft gets clamped
        // before the slides lay out) — retry until the snap actually applies.
        let attempts = 0;
        const syncInitial = () => {
          this.showSlide(this.selectedIndex);
          const slide = this.slides[this.selectedIndex];
          const target = slide.offsetLeft - this.track.offsetLeft;
          if (Math.abs(this.track.scrollLeft - target) > 2 && attempts++ < 20) {
            setTimeout(syncInitial, 150);
          }
        };
        requestAnimationFrame(syncInitial);
      }
    }

    warmUp() {
      this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    showSlide(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollLeft = slide.offsetLeft - this.track.offsetLeft;
    }

    select(index) {
      this.commit(index);
      this.showSlide(index);
    }

    commit(index) {
      this.selectedIndex = index;
      if (this.inputs[index]) this.inputs[index].checked = true;

      const slide = this.slides[index];
      if (!slide) return;
      if (this.titleLink && slide.dataset.url) this.titleLink.href = slide.dataset.url;

      const quickBuy = this.querySelector('quick-buy-modal');
      if (quickBuy && slide.dataset.variantId) {
        quickBuy.setAttribute('data-selected-variant', slide.dataset.variantId);
      }
    }
  }

  customElements.define('lineup-card', LineupCard);
}
